import React, { useState } from 'react';
import { COLORS } from './ui';

/**
 * Inline-SVG charts. Deliberately few forms and one visual system:
 *   - horizontal bars for magnitude by category (the label reads left to right)
 *   - grouped bars for actual vs estimated (one axis — never a second y-scale)
 *   - a column histogram for overdue ageing
 *
 * Mark rules applied: 4px rounded data-ends anchored to the baseline, a 2px
 * surface gap between adjacent fills, recessive grid, tabular figures on ticks,
 * hover tooltip on every mark, legend whenever there are two series.
 */

const AXIS_FONT = { fontSize: 11, fill: COLORS.muted, fontVariantNumeric: 'tabular-nums' };

function useTooltip() {
    const [tip, setTip] = useState(null);
    const node = tip ? (
        <div style={{
            position: 'fixed', left: tip.x + 12, top: tip.y + 12, zIndex: 50,
            background: '#fff', border: `1px solid ${COLORS.axis}`, borderRadius: 6,
            padding: '6px 9px', fontSize: 12, color: COLORS.ink, pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxWidth: 280,
        }}>{tip.content}</div>
    ) : null;
    const handlers = content => ({
        onMouseMove: e => setTip({ x: e.clientX, y: e.clientY, content }),
        onMouseLeave: () => setTip(null),
    });
    return { node, handlers };
}

const niceMax = v => {
    if (!v || v <= 0) return 1;
    const mag = Math.pow(10, Math.floor(Math.log10(v)));
    return Math.ceil(v / mag) * mag;
};

/**
 * Horizontal bars. Single series — the title names it, so no legend box.
 * Values are direct-labelled at the data end (selective: only the value, and
 * only because there are few bars).
 */
export function BarChart({ data, valueKey = 'value', labelKey = 'label', format = v => v, height = 26, maxLabel = 220, color = COLORS.series[0] }) {
    const { node, handlers } = useTooltip();
    if (!data || !data.length) return <div style={{ color: COLORS.muted, fontSize: 13 }}>No data</div>;

    const max = niceMax(Math.max(...data.map(d => Number(d[valueKey]) || 0)));
    const plotW = 460;

    return (
        <div style={{ overflowX: 'auto' }}>
            {node}
            <svg width={maxLabel + plotW + 70} height={data.length * height + 24} role="img">
                {[0, 0.25, 0.5, 0.75, 1].map(t => (
                    <line key={t} x1={maxLabel + t * plotW} x2={maxLabel + t * plotW}
                        y1={0} y2={data.length * height} stroke={COLORS.grid} strokeWidth={1} />
                ))}
                {data.map((d, i) => {
                    const v = Number(d[valueKey]) || 0;
                    // 2px gap between adjacent fills, so bars never touch.
                    const w = Math.max((v / max) * plotW, v > 0 ? 3 : 0);
                    const y = i * height + 3;
                    const barH = height - 8;
                    return (
                        <g key={i} {...handlers(<><strong>{d[labelKey]}</strong><br />{format(v)}{d.tooltip ? <><br />{d.tooltip}</> : null}</>)}>
                            <rect x={0} y={y - 3} width={maxLabel + plotW + 70} height={height} fill="transparent" />
                            <text x={maxLabel - 8} y={y + barH / 2 + 4} textAnchor="end"
                                style={{ fontSize: 11.5, fill: COLORS.ink }}>
                                {String(d[labelKey]).length > 34 ? String(d[labelKey]).slice(0, 33) + '…' : d[labelKey]}
                            </text>
                            <rect x={maxLabel} y={y} width={w} height={barH} rx={4} fill={d.color || color} />
                            <text x={maxLabel + w + 6} y={y + barH / 2 + 4} style={{ fontSize: 11, fill: COLORS.ink2, fontVariantNumeric: 'tabular-nums' }}>
                                {format(v)}
                            </text>
                        </g>
                    );
                })}
                <line x1={maxLabel} x2={maxLabel} y1={0} y2={data.length * height} stroke={COLORS.axis} strokeWidth={1} />
            </svg>
        </div>
    );
}

/** Grouped bars, two series, one axis. Legend is always present at two series. */
export function GroupedBarChart({ data, seriesA, seriesB, labelKey = 'label', format = v => v, maxLabel = 200 }) {
    const { node, handlers } = useTooltip();
    if (!data || !data.length) return <div style={{ color: COLORS.muted, fontSize: 13 }}>No data</div>;

    const max = niceMax(Math.max(...data.flatMap(d => [Number(d[seriesA.key]) || 0, Number(d[seriesB.key]) || 0])));
    const plotW = 420, rowH = 34, barH = 12;

    return (
        <div style={{ overflowX: 'auto' }}>
            {node}
            <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 12, color: COLORS.ink2 }}>
                {[seriesA, seriesB].map(s => (
                    <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, display: 'inline-block' }} />
                        {s.label}
                    </span>
                ))}
            </div>
            <svg width={maxLabel + plotW + 90} height={data.length * rowH + 20}>
                {[0, 0.5, 1].map(t => (
                    <line key={t} x1={maxLabel + t * plotW} x2={maxLabel + t * plotW}
                        y1={0} y2={data.length * rowH} stroke={COLORS.grid} strokeWidth={1} />
                ))}
                {data.map((d, i) => {
                    const y = i * rowH + 3;
                    const a = Number(d[seriesA.key]) || 0, b = Number(d[seriesB.key]) || 0;
                    return (
                        <g key={i} {...handlers(<>
                            <strong>{d[labelKey]}</strong><br />
                            {seriesA.label}: {format(a)}<br />{seriesB.label}: {format(b)}
                        </>)}>
                            <rect x={0} y={y - 3} width={maxLabel + plotW + 90} height={rowH} fill="transparent" />
                            <text x={maxLabel - 8} y={y + 15} textAnchor="end" style={{ fontSize: 11.5, fill: COLORS.ink }}>
                                {String(d[labelKey]).length > 30 ? String(d[labelKey]).slice(0, 29) + '…' : d[labelKey]}
                            </text>
                            <rect x={maxLabel} y={y} width={Math.max((a / max) * plotW, a > 0 ? 3 : 0)} height={barH} rx={4} fill={seriesA.color} />
                            {/* 2px surface gap between the two fills */}
                            <rect x={maxLabel} y={y + barH + 2} width={Math.max((b / max) * plotW, b > 0 ? 3 : 0)} height={barH} rx={4} fill={seriesB.color} />
                            <text x={maxLabel + Math.max((a / max) * plotW, 3) + 6} y={y + barH - 1}
                                style={{ fontSize: 10.5, fill: COLORS.ink2, fontVariantNumeric: 'tabular-nums' }}>{format(a)}</text>
                        </g>
                    );
                })}
                <line x1={maxLabel} x2={maxLabel} y1={0} y2={data.length * rowH} stroke={COLORS.axis} strokeWidth={1} />
            </svg>
        </div>
    );
}

/** Column histogram — one series, ordered buckets. */
export function ColumnChart({ data, valueKey = 'value', labelKey = 'label', color = COLORS.series[0], height = 160 }) {
    const { node, handlers } = useTooltip();
    if (!data || !data.length) return <div style={{ color: COLORS.muted, fontSize: 13 }}>No data</div>;

    const max = niceMax(Math.max(...data.map(d => Number(d[valueKey]) || 0)));
    const colW = 62, plotH = height;

    // Headroom for the value label above the tallest column. Without it a bar
    // at full height puts its own label at y=-5, outside the viewport, so the
    // largest number on the chart is the one you cannot read.
    const TOP_PAD = 14;

    return (
        <div style={{ overflowX: 'auto' }}>
            {node}
            <svg width={data.length * colW + 50} height={plotH + 46 + TOP_PAD}>
                <g transform={`translate(0,${TOP_PAD})`}>
                {[0, 0.5, 1].map(t => (
                    <g key={t}>
                        <line x1={40} x2={data.length * colW + 40} y1={plotH - t * plotH} y2={plotH - t * plotH}
                            stroke={COLORS.grid} strokeWidth={1} />
                        <text x={34} y={plotH - t * plotH + 4} textAnchor="end" style={AXIS_FONT}>
                            {Math.round(max * t).toLocaleString()}
                        </text>
                    </g>
                ))}
                {data.map((d, i) => {
                    const v = Number(d[valueKey]) || 0;
                    const h = Math.max((v / max) * plotH, v > 0 ? 3 : 0);
                    const x = 40 + i * colW + 8;
                    return (
                        <g key={i} {...handlers(<><strong>{d[labelKey]}</strong><br />{v.toLocaleString()} work orders</>)}>
                            <rect x={x - 8} y={0} width={colW} height={plotH + 40} fill="transparent" />
                            <rect x={x} y={plotH - h} width={colW - 18} height={h} rx={4} fill={d.color || color} />
                            <text x={x + (colW - 18) / 2} y={plotH - h - 5} textAnchor="middle"
                                style={{ fontSize: 11, fill: COLORS.ink2, fontVariantNumeric: 'tabular-nums' }}>{v.toLocaleString()}</text>
                            <text x={x + (colW - 18) / 2} y={plotH + 16} textAnchor="middle" style={AXIS_FONT}>{d[labelKey]}</text>
                        </g>
                    );
                })}
                <line x1={40} x2={data.length * colW + 40} y1={plotH} y2={plotH} stroke={COLORS.axis} strokeWidth={1} />
                </g>
            </svg>
        </div>
    );
}
